import { useEffect, useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, Send, ArrowLeftRight, Repeat, ExternalLink, X, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { getBscScanUrl } from "@/lib/web3Client";

const fmt = (v, d = 8) =>
  Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: d });

const STATUS_STYLE = {
  completed:    "bg-emerald-50 text-emerald-700",
  confirming:   "bg-blue-50 text-cm-blue",
  broadcasting: "bg-blue-50 text-cm-blue",
  pending_admin:"bg-amber-50 text-amber-700",
  rejected:     "bg-red-50 text-red-700",
  cancelled:    "bg-zinc-100 text-cm-grey",
};

const CAT_META = {
  deposit:     { Icon: ArrowDownToLine, label: "Deposit",   tone: "in"  },
  withdraw:    { Icon: ArrowUpFromLine, label: "Withdraw",  tone: "out" },
  transfer:    { Icon: Send,            label: "Transfer",  tone: "auto"},
  spot_trade:  { Icon: Repeat,          label: "Spot Trade",tone: "auto"},
  p2p_trade:   { Icon: ArrowLeftRight,  label: "Trade", tone: "auto"},
};

export default function HistoryTable() {
  const [rows, setRows] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 15;

  const load = async () => {
    try {
      const params = { limit, skip };
      if (filterType !== "all") params.type = filterType;
      const r = await api.get("/api/wallet/movements", { params });
      setRows(r.data.movements || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      // Don't blank out an existing list on a transient error.
      if (rows === null) setRows([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterType, skip]);

  // Auto-refresh every 8s so confirming → completed transitions show live
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, skip]);

  const cancelWithdrawal = async (txId) => {
    if (!window.confirm("Cancel this withdrawal? Your balance will be refunded.")) return;
    try {
      await api.post(`/api/wallet/withdraw/${txId}/cancel`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const filters = [
    { k: "all",      label: "All" },
    { k: "deposit",  label: "Deposits" },
    { k: "withdraw", label: "Withdrawals" },
    { k: "transfer", label: "Transfers" },
    { k: "spot",     label: "Spot" },
  ];

  return (
    <div className="bg-white rounded-[16px] border border-[var(--cm-border)]">
      <div className="px-5 py-4 border-b border-[var(--cm-border)] flex items-center gap-3 flex-wrap">
        <h2 className="text-[15px] font-bold text-cm-navy">Transaction History</h2>
        <div className="flex-1" />
        <div className="inline-flex bg-cm-surface rounded-[10px] p-0.5 flex-wrap">
          {filters.map((f) => (
            <button
              key={f.k}
              onClick={() => { setSkip(0); setFilterType(f.k); }}
              className={`px-3 py-1.5 rounded-[8px] text-[12px] font-semibold ${
                filterType === f.k
                  ? "bg-white text-cm-navy shadow-sm"
                  : "text-cm-grey hover:text-cm-navy"
              }`}
              data-testid={`history-filter-${f.k}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {rows === null ? (
        <div className="px-5 py-12 text-center text-cm-grey">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="px-5 py-12 text-center text-cm-grey">
          No {filterType === "all" ? "" : filters.find(f => f.k === filterType)?.label.toLowerCase() + " "}transactions yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-cm-grey text-left">
              <tr>
                <th className="px-5 py-3 font-semibold">Type</th>
                <th className="px-5 py-3 font-semibold">Asset</th>
                <th className="px-5 py-3 font-semibold text-right">Amount</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Details</th>
                <th className="px-5 py-3 font-semibold">Time</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--cm-border)]">
              {rows.map((m) => {
                const meta = CAT_META[m.category] || { Icon: Send, label: m.category, tone: "auto" };
                const Icon = meta.Icon;
                const isOut = meta.tone === "out" || (meta.tone === "auto" && m.direction === "out");
                return (
                  <tr key={m.id} data-testid={`history-row-${m.id}`}>
                    <td className="px-5 py-3">
                      <div className="inline-flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-full grid place-items-center ${
                          isOut ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-700"
                        }`}>
                          <Icon size={13} />
                        </span>
                        <span className="font-semibold text-cm-navy">{meta.label}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-cm-navy font-semibold">{m.asset}</span>
                      {m.network && m.network !== "internal" && (
                        <span className="ml-1 text-[11px] text-cm-grey">·{m.network}</span>
                      )}
                      {m.category === "spot_trade" && (
                        <span className="ml-1 text-[11px] text-cm-grey">/ {m.quote_asset}</span>
                      )}
                      {m.category === "p2p_trade" && (
                        <span className="ml-1 text-[11px] text-cm-grey">/ {m.fiat_asset}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      <span className={isOut ? "text-red-600" : "text-emerald-700"}>
                        {isOut ? "-" : "+"}{fmt(m.amount)}
                      </span>
                      {Number(m.fee || 0) > 0 && (
                        <div className="text-[10px] text-cm-grey">
                          fee: {fmt(m.fee)}{m.fee_asset ? ` ${m.fee_asset}` : ""}
                        </div>
                      )}
                      {m.category === "spot_trade" && (
                        <div className="text-[10px] text-cm-grey">@ {fmt(m.price, 4)} {m.quote_asset}</div>
                      )}
                      {m.category === "p2p_trade" && (
                        <div className="text-[10px] text-cm-grey">{fmt(m.fiat_amount)} {m.fiat_asset} @ {fmt(m.price, 2)}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[m.status] || "bg-zinc-100 text-cm-grey"}`}>
                        {m.status === "confirming" || m.status === "broadcasting"
                          ? `${m.status} ${m.confirmations || 0}/${m.required_confirmations || 0}`
                          : m.status}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[11px] text-cm-grey font-mono">
                      {m.tx_hash && (
                        <span title={m.tx_hash} className="inline-flex items-center gap-1">
                          {m.network === "BEP20" ? (
                            <a href={getBscScanUrl(m.tx_hash)} target="_blank" rel="noopener noreferrer" className="hover:text-cm-blue inline-flex items-center gap-1 group">
                              {String(m.tx_hash).slice(0, 8)}…{String(m.tx_hash).slice(-6)}
                              <ExternalLink size={10} className="opacity-60 group-hover:opacity-100" />
                            </a>
                          ) : (
                            <>
                              {String(m.tx_hash).slice(0, 8)}…{String(m.tx_hash).slice(-6)}
                              <ExternalLink size={10} className="opacity-60" />
                            </>
                          )}
                        </span>
                      )}
                      {m.to_address && !m.tx_hash && (
                        <span title={m.to_address}>
                          → {String(m.to_address).slice(0, 8)}…{String(m.to_address).slice(-6)}
                        </span>
                      )}
                      {m.counterparty_email && (
                        <span>{m.direction === "out" ? "→ " : "← "}{m.counterparty_email}</span>
                      )}
                      {m.category === "spot_trade" && (
                        <span className="capitalize">{m.side} · {m.role}</span>
                      )}
                      {m.category === "p2p_trade" && (
                        <span className="capitalize">{m.side}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-cm-grey whitespace-nowrap">
                      {new Date(m.created_at).toLocaleString("en-US", {
                        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                      })}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {m.category === "withdraw" && m.status === "pending_admin" && (
                        <button
                          onClick={() => cancelWithdrawal(m.id)}
                          className="text-red-600 hover:bg-red-50 px-2 py-1 rounded inline-flex items-center gap-1 text-[12px] font-semibold"
                          data-testid={`history-cancel-${m.id}`}
                        >
                          <X size={12} /> Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="px-5 py-3 border-t border-[var(--cm-border)] flex items-center justify-between text-[12px]">
          <span className="text-cm-grey">
            {skip + 1}–{Math.min(skip + limit, total)} of {total}
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setSkip(Math.max(0, skip - limit))}
              disabled={skip === 0}
              className="px-3 py-1 rounded-[8px] border border-[var(--cm-border)] disabled:opacity-40"
              data-testid="history-prev"
            >
              Prev
            </button>
            <button
              onClick={() => setSkip(skip + limit)}
              disabled={skip + limit >= total}
              className="px-3 py-1 rounded-[8px] border border-[var(--cm-border)] disabled:opacity-40"
              data-testid="history-next"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
